import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AuthImg } from "@/lib/authMedia";
import { chooseFaceLockAnimated, type FaceLockAvatar } from "@/lib/facelock";

// "Choose its animated picture" (owner, 2026-10-11: the card showed version 1
// with its eyes off while version 3 of the same draw was better). Every version
// the draw kept, large; its owner (or an admin) picks the one its card shows.
// One the server flagged as broken says so and can still be picked: people
// judge, a score never does. Free.

type Props = {
  avatar: FaceLockAvatar | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A pick was saved: read the list again. */
  onChanged: () => void;
};

export default function AnimatedChooseDialog({ avatar, open, onOpenChange, onChanged }: Props) {
  const { t } = useTranslation();
  const a = avatar?.animated_picture ?? null;
  const choices = React.useMemo(() => a?.choices ?? [], [a]);
  const shown = a?.chosen_index ?? null;
  const [selected, setSelected] = React.useState<number | null>(shown);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setSelected(shown);
    setError("");
  }, [avatar?.id, shown, open]);

  if (!avatar || !a || !a.url || choices.length < 2) return null;

  const save = async () => {
    if (selected === null || busy) return;
    if (selected === shown) { onOpenChange(false); return; }
    setBusy(true);
    setError("");
    const res = await chooseFaceLockAnimated(avatar.id, selected);
    setBusy(false);
    if (res.ok === false) {
      if (res.error === "picture_unknown") { setError(t("faceLock.errors.picture_unknown")); onChanged(); return; }
      setError(t("faceLock.animated.chooseFailed"));
      return;
    }
    onChanged();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent overlayClassName="z-[400]" className="z-[400] max-h-[92vh] overflow-y-auto sm:max-w-4xl"
        data-testid="facelock-animated-choose-dialog">
        <DialogHeader>
          <DialogTitle>{t("faceLock.animated.chooseTitle", { name: avatar.name })}</DialogTitle>
          <DialogDescription>{t("faceLock.animated.chooseLead")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {choices.map((c, n) => {
            const on = selected === c.index;
            return (
              <button
                key={c.index}
                type="button"
                data-testid={`facelock-animated-choice-${c.index}`}
                aria-pressed={on}
                aria-label={t("faceLock.animated.chooseOption", { n: n + 1 })}
                onClick={() => setSelected(c.index)}
                className={`relative overflow-hidden rounded-lg border-2 text-left ${on ? "border-primary" : "border-transparent"}`}
              >
                <AuthImg src={c.url} alt={t("faceLock.animated.chooseOption", { n: n + 1 })} loading="lazy"
                  style={{ width: "100%", aspectRatio: "3 / 4", objectFit: "cover", display: "block" }} />
                <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[11px] text-white">
                  {t("faceLock.animated.chooseOption", { n: n + 1 })}
                </span>
                {on && <Check className="absolute right-1 top-1 h-5 w-5 rounded-full bg-primary p-0.5 text-primary-foreground" aria-hidden />}
                {c.broken && (
                  <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-amber-600/90 px-1.5 py-1 text-[11px] text-white">
                    <AlertTriangle className="h-3 w-3" aria-hidden /> {t("faceLock.animated.chooseBroken")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {error && <div role="alert" className="text-sm text-red-500" data-testid="facelock-animated-choose-error">{error}</div>}
        <div className="flex justify-end">
          <Button type="button" onClick={() => void save()} disabled={busy || selected === null} data-testid="facelock-animated-choose-save">
            {t("faceLock.animated.chooseSave")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
